import { TemplatesManager, UserSettingsManager } from '@go-widgets/well-logs-widget';
import { makeObservable, observable, runInAction } from 'mobx';

import { requireService } from 'src/packages/di';

import type { WellIndexType, DashboardService, TemplatesManagerEventHandlers } from '@go-widgets/well-logs-widget';

import { BroadcastReadTunnel } from '../broadcast-tunnel/BroadcastReadTunnel';

export type TWellLogsUpdateTemplatesNotification = {
  type: 'well-logs-update-templates';
  data: {
    indexType: WellIndexType;
  };
};

export class WellLogsTemplatesManager {
  readonly type: WellIndexType;
  readonly templatesManager: TemplatesManager;
  readonly userSettings: UserSettingsManager;
  readonly api: DashboardService;
  readonly templatesUpdatingChannel: BroadcastReadTunnel<TWellLogsUpdateTemplatesNotification>;

  @observable isLoading: boolean = false;

  constructor(
    type: WellIndexType,
    api: DashboardService,
    templatesManagerEventHandlers: TemplatesManagerEventHandlers,
    private readonly authService = requireService('authService'),
    private readonly notifications = requireService('notifications')
  ) {
    this.api = api;
    this.type = type;
    this.userSettings = new UserSettingsManager(this.api);
    this.templatesManager = new TemplatesManager(this.userSettings, this.api, this.type, templatesManagerEventHandlers);

    this.templatesUpdatingChannel = new BroadcastReadTunnel(
      (message): message is TWellLogsUpdateTemplatesNotification => {
        if (message?.type !== 'well-logs-update-templates' || message?.data?.indexType !== this.type) {
          return false;
        }

        return true;
      },
      this.onTemplatesUpdate
    );

    makeObservable(this);
  }

  private onTemplatesUpdate = async (): Promise<void> => {
    try {
      runInAction(() => {
        this.isLoading = true;
      });

      await this.templatesManager.reloadList();
    } catch (error) {
      console.error(error);
      this.notifications.showErrorMessageT('errors:failedToLoadTemplateList');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  init = async () => {
    try {
      runInAction(() => {
        this.isLoading = true;
      });
      const ownerUserId = this.authService.userInfo.sub;

      await this.userSettings.loadSettings(ownerUserId, 'CURVE', this.type);
      await this.templatesManager.reloadList();
    } catch (error) {
      console.error(error);
      this.notifications.showErrorMessageT('errors:failedToLoadTemplateList');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  dispose = (): void => {
    return this.templatesUpdatingChannel.disconnect();
  };
}
